import { authedQuery, gql } from '@crystal-eyes/utils/apis/graphqlApi'
import { useRef } from 'react'
import usePersonalInfo from '@crystal-eyes/hooks/profiles/usePersonalInfo'
import { isLICDNPhoto } from '@crystal-eyes/lib/url'
import { uploadFileToS3 } from '@crystal-eyes/lib/s3'

interface Identity {
  id: string
}

type Options = {
  identity: Identity
}

type State = {
  data: {
    maybePersistPhoto: () => void
    photoUrl?: string
  }
}

const UPDATE_PROFILE_S3_PHOTO_KEY = gql`
  mutation UpdateProfileS3PhotoKey($id: Guid!, $key: String!) {
    updatePhotoS3Key(id: $id, key: $key) {
      id
      personalInfo {
        photoUrl
      }
    }
  }
`

const updateProfile = async (id: string, key: string) => {
  return authedQuery(UPDATE_PROFILE_S3_PHOTO_KEY, {
    id: id,
    key: key,
  })
}

const uploadPhoto = async (photoUrl: string, identityId: string) => {
  const key = `${identityId}.jpg`
  try {
    const response = await fetch(photoUrl)
    if (!validateImageResponse(response))
      throw new Error('Invalid image response')
    const blob = await response.blob()
    const file = new File([blob], key, { type: 'image/jpeg' })
    const uploaded = await uploadFileToS3(key, file)
    if (uploaded) await updateProfile(identityId, key)
  } catch (e) {
    /* eslint-disable no-console */
    console.log(e)
  }
}

const validateImageResponse = (response: Response) => {
  const isJpeg = response.headers.get('content-type')?.includes('jpeg')
  const isOk = response.status === 200
  const contentLength = response.headers.get('content-length')
  return (
    isJpeg &&
    isOk &&
    contentLength &&
    parseInt(contentLength) > 0 &&
    parseInt(contentLength) < 100000
  )
}

export default function usePersistProfilePhoto({ identity }: Options): State {
  const { data: personalInfo } = usePersonalInfo({ identity })
  const attemptedRef = useRef(new Set<string>())
  const maybePersistPhoto = async () => {
    if (
      !attemptedRef.current.has(identity.id) &&
      personalInfo?.photoUrl &&
      isLICDNPhoto(personalInfo.photoUrl)
    ) {
      attemptedRef.current.add(identity.id)
      await uploadPhoto(personalInfo.photoUrl, identity.id)
    }
  }

  return {
    data: {
      maybePersistPhoto,
      photoUrl: personalInfo?.photoUrl,
    },
  }
}
